import { useEffect } from "react";
import { Button, Checkbox, useModal } from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { FilterSection } from "@/search/Langston/FilterPanel/FilterSection";
import { useConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";
import { ConceptTaxonomyModal } from "./ConceptTaxonomyModal";

import {
  checkboxCss,
  listContainerCss,
  listItemCss,
  conceptsContainerCss,
  viewAllButtonCss,
} from "./Concepts.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);
const CheckboxWithTrackEvent = withTrackEvent(Checkbox);

const eventPropertiesViewAll = {
  objectType: objectTypes.user,
  actionName: actionNames.interact,
  streamProperties: {
    component_type: "TopicsViewAll",
    description: "Open Taxonomy Modal",
  },
} as const;

export const Concepts = () => {
  const [modalProps, setActive, triggerRef] = useModal();

  const {
    getters: { isDisabledFilters, isSearchLoading },
  } = useSearchPageContext();

  const {
    selectedIds,
    setDraftSelectedIds,
    setSelectedIds,
    topFiveElaTags,
    updateSearchParams,
  } = useConceptTaxonomyProvider();

  useEffect(() => {
    if (modalProps.isActive) {
      // Initialize draft from current selection state.
      setDraftSelectedIds(new Set(selectedIds));
    }
  }, [modalProps.isActive]);

  if (!topFiveElaTags.length) return null;

  const handleSelect = (value: string, checked: boolean) => {
    if (checked) {
      const newState = new Set([...selectedIds, value]);
      setSelectedIds(newState);
      updateSearchParams(newState);
    } else {
      const newSelectedIds = new Set(selectedIds);
      newSelectedIds.delete(value);
      setSelectedIds(newSelectedIds);
      updateSearchParams(newSelectedIds);
    }
  };

  return (
    <>
      <FilterSection title="Topics">
        <div css={conceptsContainerCss}>
          <ul css={listContainerCss}>
            {topFiveElaTags?.map((conceptTag) => (
              <li key={conceptTag.id} css={listItemCss}>
                <CheckboxWithTrackEvent
                  __cssFor={checkboxCss}
                  label={conceptTag.name}
                  value={conceptTag.id}
                  checked={selectedIds.has(conceptTag.id)}
                  onChange={(e) =>
                    handleSelect(e.target.value, e.target.checked)
                  }
                  objectType={objectTypes.content}
                  actionName={actionNames.filter}
                  streamProperties={{
                    description: "Select conceptTag for search query",
                  }}
                  dataProps={{
                    "data-qa-selector": "select-concept-filter",
                    "data-testid": `checkbox-${conceptTag.id}`,
                  }}
                  disabled={isSearchLoading || isDisabledFilters}
                />
              </li>
            ))}
          </ul>
          <ButtonWithTrackEvent
            {...eventPropertiesViewAll}
            size={Button.sizes.sm}
            __cssFor={viewAllButtonCss}
            flavor={Button.flavors.tertiary}
            onClick={() => setActive(true)}
            ref={triggerRef}
            disabled={isDisabledFilters}
            dataProps={{
              "data-qa-selector": "view-all-open-taxonomy-modal",
              "data-testid": "view-all-button",
            }}
          >
            View all
          </ButtonWithTrackEvent>
        </div>
      </FilterSection>
      <ConceptTaxonomyModal modalProps={modalProps} setActive={setActive} />
    </>
  );
};
